import { getHttpClient } from '@/services/httpClientProvider';
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/user';
import type {
    Form,
    FormRevision,
    FormWithRevisions,
    CreateFormParams,
    UpdateFormParams,
    DeleteFormParams,
    FetchFormsParams,
    CreateFormRevisionParams,
    UpdateFormRevisionParams,
    FetchFormRevisionsParams,
    FetchFormSubmissionsParams,
    FormSubmission,
    FormTheme,
    CreateThemeParams,
    UpdateThemeParams,
    FetchThemesParams,
    DeleteThemeParams,
} from './types/forms';
import { JWTHttpClient } from '@/services/JWTHttpClient';

export enum ActionTag {
    JOURNEY = 'journey',
    CASE = 'case',
}

export function getActionTags(showInJourneys: boolean, showInCases: boolean) {
    const actionTags = [];
    if (showInJourneys) {
        actionTags.push(ActionTag.JOURNEY);
    }
    if (showInCases) {
        actionTags.push(ActionTag.CASE);
    }
    return actionTags;
}

// Get the tasks service URL from environment variable, fallback to stage API Gateway
export const FORMS_SERVICE_URL =
    import.meta.env.VITE_FORMS_SERVICE_URL || 'https://forms-230937369904.us-central1.run.app';
// Create JWT HTTP client for Task Service API calls
const jwtHttpClient = new JWTHttpClient(FORMS_SERVICE_URL);

/**
 * Composable that provides access to Forms API functions.
 * This safely initializes the httpClient with authStore only when called
 * inside components or other composables after Pinia is ready.
 */
export function useFormsAPI() {
    const authStore = useAuthStore();
    const httpClient = getHttpClient(authStore);

    return {
        async fetchForms(params: FetchFormsParams = {}): Promise<Form[]> {
            if (!params.orgId) {
                console.warn('⚠️ FormsAPI: fetchForms called with undefined orgId');
                console.trace('Stack trace for undefined orgId:');
            }
            try {
                const response = await jwtHttpClient.get<any>(
                    'v1/forms',
                    {
                        orgId: params.orgId || '',
                    },
                    {
                        'x-ovationcxm-source': 'com.ovationcxm.forms/ui:forms',
                    }
                );
                return response;
            } catch (error) {
                console.error('Failed to fetch forms:', error);
                throw new Error('Failed to fetch forms');
            }
        },

        async createForm(params: CreateFormParams): Promise<Form> {
            try {
                console.log('📋 FormsAPI: Creating form:', params);

                // Get org ID from authenticated user
                const userStore = useUserStore();
                const orgId = userStore.userData?.context_org_id || userStore.userData?.object_id;

                if (!orgId) {
                    throw new Error('No organization ID available - user context not loaded');
                }

                const form: CreateFormParams = {
                    privateName: params.privateName,
                    publicName: params.publicName,
                    description: params.description,
                    orgId: params.orgId,
                };

                const response = await jwtHttpClient.post<Form>('v1/forms', params, undefined, {
                    'content-type': 'application/json',
                });

                console.log('📋 FormsAPI createForm: Response received:', response);
                if (response) {
                    return response;
                }

                throw new Error('Failed to create form');
            } catch (error) {
                console.error('❌ FormsAPI: Error creating form:', error);
                throw error;
            }
        },

        async updateForm(params: UpdateFormParams): Promise<Form> {
            try {
                console.log('📋 FormsAPI: Updating form:', params);

                const response = await jwtHttpClient.patch<Form>(`v1/forms/${params.id}`, params, undefined, {
                    'content-type': 'application/json',
                });

                console.log('📋 FormsAPI updateForm: Response received:', response);
                if (response) {
                    return response;
                }

                throw new Error('Failed to update form');
            } catch (error) {
                console.error('❌ FormsAPI: Error updating form:', error);
                throw error;
            }
        },

        async deleteForm(params: DeleteFormParams): Promise<boolean> {
            try {
                console.log('📋 FormsAPI: Deleting form:', params.id);

                const response = await jwtHttpClient.delete<any>(`v1/forms/${params.id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.forms/ui:forms',
                });

                console.log('📋 FormsAPI deleteForm: Response received:', response);
                return true;
            } catch (error) {
                console.error('❌ FormsAPI: Error deleting form:', error);
                throw error;
            }
        },

        async getFormById(id: string): Promise<Form> {
            try {
                console.log('📋 FormsAPI: Fetching form by ID:', id);

                const response = await jwtHttpClient.get<Form>(`v1/forms/${id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.forms/ui:forms',
                });

                console.log('📋 FormsAPI getFormById: Response received:', response);
                if (response) {
                    return response;
                }

                throw new Error('Form not found');
            } catch (error) {
                console.error('❌ FormsAPI: Error fetching form by ID:', error);
                throw error;
            }
        },

        // Form Revisions API methods
        async fetchFormSubmissions(params: FetchFormSubmissionsParams): Promise<FormSubmission[]> {
            try {
                const {
                    formId,
                    page = 1,
                    start = 0,
                    limit = 25,
                    sort = [{ property: 'created', direction: 'DESC' }],
                } = params;

                let queryParams: Record<string, string> = {};
                if ('format' in params) {
                    queryParams['format'] = params.format as string;
                    queryParams['cache'] = Date.now().toString();
                } else {
                    queryParams['format'] = 'json';
                }
                const response = await jwtHttpClient.get<FormSubmission[]>(
                    `v1/forms/${formId}/submissions`,
                    queryParams,
                    {
                        'x-ovationcxm-source': 'com.ovationcxm.forms/ui:forms',
                    }
                );

                if (response) {
                    return response;
                }

                throw new Error('Failed to fetch form submissions');
            } catch (error) {
                console.error('❌ FormsAPI: Error fetching form submissions:', error);
                throw error;
            }
        },

        async exportFormSubmissions(params: FetchFormSubmissionsParams): Promise<void> {
            try {
                const { formId, format = 'csv', fromDate, toDate } = params;

                let url =
                    FORMS_SERVICE_URL +
                    `/v1/forms/${formId}/submissions?format=${format}&cache=${Date.now().toString()}`;
                if (fromDate) {
                    url += `&fromDate=${fromDate}`;
                }
                if (toDate) {
                    url += `&toDate=${toDate}`;
                }
                window.open(url, '_blank');
            } catch (error) {
                console.error('❌ FormsAPI: Error exporting form submissions:', error);
                throw error;
            }
        },

        async fetchSubmission(id: string): Promise<FormSubmission> {
            try {
                const response = await jwtHttpClient.get<FormSubmission>(`v1/forms/-/submissions/${id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.forms/ui:forms',
                });

                if (response) {
                    return response;
                }

                throw new Error('Failed to fetch submission');
            } catch (error) {
                console.error('❌ FormsAPI: Error fetching submission:', error);
                throw error;
            }
        },

        async deleteFormSubmission(id: string): Promise<boolean> {
            try {
                console.log('📋 FormsAPI: Deleting form submission:', id);

                await jwtHttpClient.delete<any>(`v1/forms/-/submissions/${id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.forms/ui:forms',
                });

                console.log('📋 FormsAPI deleteFormSubmission: Submission deleted successfully');
                return true;
            } catch (error) {
                // Check if error is a JSON parsing error
                if (
                    error instanceof SyntaxError &&
                    typeof error.message === 'string' &&
                    error.message.toLowerCase().includes('json')
                ) {
                    return true;
                }
                console.error('❌ FormsAPI: Error deleting form submission:', error);
                throw error;
            }
        },

        async fetchTheme(orgId: string, name: string): Promise<FormTheme> {
            try {
                const queryParams: Record<string, string> = {
                    orgId: orgId,
                    name: name,
                };
                const response = await jwtHttpClient.get<FormTheme>(`v1/themes`, queryParams, {
                    'x-ovationcxm-source': 'com.ovationcxm.forms/ui:forms',
                });

                if (response) {
                    return response;
                }

                throw new Error('Failed to fetch theme');
            } catch (error) {
                console.error('❌ FormsAPI: Error fetching theme:', error);
                throw error;
            }
        },

        async createTheme(params: CreateThemeParams): Promise<FormTheme> {
            try {
                console.log('🎨 FormsAPI: Creating theme:', params);

                const response = await jwtHttpClient.post<FormTheme>('v1/themes', params, undefined, {
                    'content-type': 'application/json',
                    'x-ovationcxm-source': 'com.ovationcxm.forms/ui:forms',
                });

                console.log('🎨 FormsAPI createTheme: Response received:', response);
                if (response) {
                    return response;
                }

                throw new Error('Failed to create theme');
            } catch (error) {
                console.error('❌ FormsAPI: Error creating theme:', error);
                throw error;
            }
        },

        async updateTheme(params: UpdateThemeParams): Promise<FormTheme> {
            try {
                console.log('🎨 FormsAPI: Updating theme:', params);

                const response = await jwtHttpClient.patch<FormTheme>(`v1/themes/${params.id}`, params, undefined, {
                    'content-type': 'application/json',
                    'x-ovationcxm-source': 'com.ovationcxm.forms/ui:forms',
                });

                console.log('🎨 FormsAPI updateTheme: Response received:', response);
                if (response) {
                    return response;
                }

                throw new Error('Failed to update theme');
            } catch (error) {
                console.error('❌ FormsAPI: Error updating theme:', error);
                throw error;
            }
        },

        async fetchThemes(params: FetchThemesParams): Promise<FormTheme[]> {
            try {
                console.log('🎨 FormsAPI: Fetching themes for orgId:', params.orgId);

                const queryParams: Record<string, string> = {
                    orgId: params.orgId,
                };

                if (params.page !== undefined) {
                    queryParams.page = params.page.toString();
                }
                if (params.start !== undefined) {
                    queryParams.start = params.start.toString();
                }
                if (params.limit !== undefined) {
                    queryParams.limit = params.limit.toString();
                }
                if (params.sort) {
                    queryParams.sort = JSON.stringify(params.sort);
                }

                const response = await jwtHttpClient.get<FormTheme[]>('v1/themes', queryParams, {
                    'x-ovationcxm-source': 'com.ovationcxm.forms/ui:forms',
                });

                console.log('🎨 FormsAPI fetchThemes: Response received:', response);
                if (response) {
                    return response;
                }

                throw new Error('Failed to fetch themes');
            } catch (error) {
                console.error('❌ FormsAPI: Error fetching themes:', error);
                throw error;
            }
        },

        async deleteTheme(params: DeleteThemeParams): Promise<boolean> {
            try {
                console.log('🎨 FormsAPI: Deleting theme:', params.id);

                const response = await jwtHttpClient.delete<any>(`v1/themes/${params.id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.forms/ui:forms',
                });

                console.log('🎨 FormsAPI deleteTheme: Response received:', response);
                return true;
            } catch (error) {
                console.error('❌ FormsAPI: Error deleting theme:', error);
                throw error;
            }
        },

        // Form Revisions API methods
        async fetchFormRevisions(params: FetchFormRevisionsParams): Promise<FormRevision[]> {
            try {
                const {
                    formId,
                    page = 1,
                    start = 0,
                    limit = 25,
                    sort = [{ property: 'created', direction: 'DESC' }],
                } = params;

                // const queryParams: Record<string, string> = {
                //     page: page.toString(),
                //     start: start.toString(),
                //     limit: limit.toString(),
                //     sort: JSON.stringify(sort),
                // };

                // console.log('📋 FormsAPI: Fetching form revisions with params:', queryParams);

                const response = await jwtHttpClient.get<FormRevision[]>(`v1/forms/${formId}/revisions`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.forms/ui:forms',
                });

                // console.log('📋 FormsAPI: Form revisions response received:', {
                //     hasResults: !!response?.results,
                //     resultCount: response?.results?.length || 0,
                //     totalCount: response?.totalCount || 0,
                // });

                if (response) {
                    return response;
                }

                throw new Error('Failed to fetch form revisions');
            } catch (error) {
                console.error('❌ FormsAPI: Error fetching form revisions:', error);
                throw error;
            }
        },

        async createFormRevision(params: CreateFormRevisionParams): Promise<FormRevision> {
            try {
                console.log('📋 FormsAPI: Creating form revision:', params);

                const revision = {
                    formId: params.formId,
                    form: params.form ?? {},
                    status: params.status || 'DRAFT',
                    notes: params.notes,
                };

                const response = await jwtHttpClient.post<FormRevision>(`v1/forms/-/revisions`, revision, undefined, {
                    'content-type': 'application/json',
                });

                console.log('📋 FormsAPI createFormRevision: Response received:', response);
                if (response) {
                    return response;
                }

                throw new Error('Failed to create form revision');
            } catch (error) {
                console.error('❌ FormsAPI: Error creating form revision:', error);
                throw error;
            }
        },

        async updateFormRevision(params: UpdateFormRevisionParams): Promise<FormRevision> {
            try {
                console.log('📋 FormsAPI: Updating form revision:', params);

                const response = await jwtHttpClient.patch<FormRevision>(
                    `v1/forms/-/revisions/${params.id}`,
                    params,
                    undefined,
                    {
                        'content-type': 'application/json',
                    }
                );

                console.log('📋 FormsAPI updateFormRevision: Response received:', response);
                if (response) {
                    return response;
                }

                throw new Error('Failed to update form revision');
            } catch (error) {
                console.error('❌ FormsAPI: Error updating form revision:', error);
                throw error;
            }
        },

        async deleteFormRevision(revisionId: string): Promise<boolean> {
            try {
                await jwtHttpClient.delete(`v1/forms/-/revisions/${revisionId}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.forms/ui:forms',
                });
                return true;
            } catch (error) {
                if (error instanceof SyntaxError && /JSON/.test(error.message)) {
                    // JSON parse error while deleting form revision is expected
                    return true;
                } else {
                    console.error('❌ FormsAPI: Error deleting form revision:', error);
                }
                return false;
            }
        },

        async getFormRevisionById(id: string): Promise<FormRevision> {
            try {
                console.log('📋 FormsAPI: Fetching form revision by ID:', id);

                const response = await jwtHttpClient.get<FormRevision>(`v1/forms/-/revisions/${id}`, undefined, {
                    'x-ovationcxm-source': 'com.ovationcxm.forms/ui:forms',
                });

                console.log('📋 FormsAPI getFormRevisionById: Response received:', response);
                if (response) {
                    return response;
                }

                throw new Error('Form revision not found');
            } catch (error) {
                console.error('❌ FormsAPI: Error fetching form revision by ID:', error);
                throw error;
            }
        },

        async getFormWithRevisions(formId: string): Promise<FormWithRevisions> {
            try {
                console.log('📋 FormsAPI: Fetching form with revisions:', formId);

                // Fetch the form
                const form = await this.getFormById(formId);
                console.log('📋 FormsAPI: getFormWithRevisions Form fetched:', form);

                // Fetch the revisions
                const revisionsResponse = await this.fetchFormRevisions({ formId });
                console.log('📋 FormsAPI: getFormWithRevisions Revisions fetched:', revisionsResponse);
                const reversedRevisionsResponse = Array.isArray(revisionsResponse)
                    ? [...revisionsResponse].reverse()
                    : [];

                // Get the most recent revision
                const currentRevision = reversedRevisionsResponse.length > 0 ? reversedRevisionsResponse[0] : undefined;

                const formWithRevisions: FormWithRevisions = {
                    ...form,
                    revisions: reversedRevisionsResponse,
                    currentRevision,
                };

                console.log('✅ FormsAPI: Form with revisions fetched successfully:', formWithRevisions);
                return formWithRevisions;
            } catch (error) {
                console.error('❌ FormsAPI: Error fetching form with revisions:', error);
                throw error;
            }
        },
    };
}
